/**
 * LoadingState — a skeleton shaped like the content it replaces, plus the announcement a skeleton
 * cannot make by itself.
 *
 * TWO THINGS THIS COMPONENT EXISTS TO STOP.
 *
 *   1. A SILENT WAIT. A skeleton is purely visual. Without a live region a screen-reader user is
 *      told nothing at all between pressing a button and the content arriving, which reads as the
 *      app having ignored them. `aria-busy` plus a polite live region fixes it once.
 *
 *   2. A STATE THAT ONLY EXISTS WHILE IT ANIMATES. web/ui's global stylesheet neutralises every
 *      animation under `prefers-reduced-motion` — "nothing may exist ONLY during an animation. A
 *      skeleton, a toast or a slide-in panel must be readable at frame zero"
 *      (docs/ui/01-tokens.md). Mantine's `Skeleton` animates by default, so `animate` is
 *      not what carries the meaning here: the shape and the live region do.
 *
 * The default is three ragged lines, which is the shape of a text block; pass `lines` and `height`
 * for anything else. There is no spinner variant on purpose — a spinner tells the reader nothing
 * about what is coming.
 */
import { Box, Skeleton, Stack, VisuallyHidden } from '@mantine/core';
import { useUiText } from '../../texts/ui-texts';

export interface LoadingStateProps {
  /** What is loading, in the person's words: "your cabinet", "the article list". */
  label: string;
  /** How many skeleton lines. Match the content it stands in for. */
  lines?: number;
  /** Line height, any CSS length. */
  height?: number | string;
}

export function LoadingState({ label, lines = 3, height = 14 }: LoadingStateProps) {
  const t = useUiText();
  const rows = Array.from({ length: Math.max(1, lines) }, (_, index) => index);
  const lastIndex = rows.length - 1;

  return (
    <Box aria-busy="true">
      {/*
        Polite, not assertive: a load in progress is information, not an interruption. It is a
        sibling of the skeleton rather than a label on it, because a live region that is itself
        replaced by the content stops announcing.
      */}
      <VisuallyHidden>
        <div role="status" aria-live="polite">
          {t('loadingState.loading', { what: label })}
        </div>
      </VisuallyHidden>
      <Stack gap="xs" aria-hidden="true">
        {rows.map((index) => (
          <Skeleton
            key={index}
            height={height}
            radius="sm"
            /* A ragged last line is what a paragraph looks like; a full-width one looks like a bug. */
            width={index === lastIndex && rows.length > 1 ? '62%' : '100%'}
          />
        ))}
      </Stack>
    </Box>
  );
}
